import React from "react";
import { Link } from "react-router-dom";
import CodeBlock, { CodeText } from "../../components/CodeBlock";
import { Tab } from "../../components/CodeBlock/CodeBlock";
import useScrollToHash from "../../utils/scrollToHash";
import SectionLinkCopy from "../../components/SectionLinkCopy";

const Variable = () => {
    useScrollToHash();
    return (
        <>
            <section className="min-h-screen bg-secondary-dark flex flex-wrap pt-[14vh] pb-5 md:pt-[15vh] px-4 md:px-11 flex-col items-centers">
                <div className="text-secondary text-3xl md:text-6xl mx-auto">
                    Variable
                </div>
                <div className="text-white text-base md:text-2xl mt-4 max-w-full">
                    <p>
                        <Tab />
                        Variables in JMC is simply a fake player in scoreboard
                        of vanilla minecraft, hence it can only represents
                        integer. It always start with <code>$</code>.
                        Alternatively, you can use your custom scoreboard
                        objective by using <code>objective:selector</code>{" "}
                        syntax.
                    </p>
                </div>
                <section id="variable_assignment" />
                <div className="text-secondary-contrast text-xl md:text-4xl mt-3 md:mt-4">
                    Variable Assignment
                    <SectionLinkCopy sectionId="variable_assignment" />
                </div>
                <div className="text-white text-base md:text-2xl mt-4 max-w-full">
                    <p>
                        <Tab />
                        Set a variable to an integer. Due to nature of how
                        minecraft scoreboard works, you can assign a variable
                        without declaring it.
                    </p>

                    <CodeBlock>
                        <CodeText type="variable">{"$<variable>"}</CodeText>{" "}
                        <CodeText type="operator">=</CodeText>{" "}
                        <CodeText type="number">{"<integer>"}</CodeText>;
                    </CodeBlock>
                    <CodeBlock>
                        <CodeText type="variable">$my_variable</CodeText>{" "}
                        <CodeText type="operator">=</CodeText>{" "}
                        <CodeText type="number">5</CodeText>;
                    </CodeBlock>
                    <CodeBlock>
                        scores
                        <CodeText type="operator">:</CodeText>
                        <CodeText type="param">@s</CodeText>{" "}
                        <CodeText type="operator">=</CodeText>{" "}
                        <CodeText type="number">5</CodeText>;
                    </CodeBlock>
                    <CodeBlock>
                        <CodeText type="variable">$my_variable</CodeText>{" "}
                        <CodeText type="operator">=</CodeText>{" "}
                        <CodeText type="operator">true</CodeText>;{" "}
                        <CodeText type="comment">
                            {"//"} $my_variable = 1
                        </CodeText>
                    </CodeBlock>
                    <CodeBlock>
                        <CodeText type="variable">$my_variable</CodeText>{" "}
                        <CodeText type="operator">=</CodeText>{" "}
                        <CodeText type="operator">false</CodeText>;{" "}
                        <CodeText type="comment">
                            {"//"} $my_variable = 0
                        </CodeText>
                    </CodeBlock>
                    <CodeBlock>
                        <CodeText type="variable">$var1</CodeText>{" "}
                        <CodeText type="operator">=</CodeText>{" "}
                        <CodeText type="variable">$var2</CodeText>{" "}
                        <CodeText type="operator">=</CodeText> scores
                        <CodeText type="operator">:</CodeText>
                        <CodeText type="param">@s</CodeText>{" "}
                        <CodeText type="operator">=</CodeText>{" "}
                        <CodeText type="number">5</CodeText>;
                    </CodeBlock>
                </div>
                <section id="variable_operation" />
                <div className="text-secondary-contrast text-xl md:text-4xl mt-3 md:mt-4">
                    Variable Operation
                    <SectionLinkCopy sectionId="variable_operation" />
                </div>
                <div className="text-white text-base md:text-2xl mt-4 max-w-full">
                    <p>
                        <Tab />
                        Perform scoreboard operations, with 10 available
                        operations.
                    </p>
                    <ul className="ml-4 md:ml-6 list-disc list-inside">
                        <li>
                            <code>=</code> Assign: Set target's score to
                            source's score
                        </li>
                        <li>
                            <code>+=</code> Addition: Add source's score to
                            target's score
                        </li>
                        <li>
                            <code>-=</code> Subtraction: Subtract source's score
                            from target's score
                        </li>
                        <li>
                            <code>*=</code> Multiplication: Set target's score
                            to the product of the target's and source's scores
                        </li>
                        <li>
                            <code>/=</code> (Integer) Division: Divide target's
                            score by source' scores, and the result will be
                            rounded down to an integer.
                        </li>
                        <li>
                            <code>%=</code> Modulus: Divide target's score by
                            source's score, and use the positive remainder to
                            set the target score.
                        </li>
                        <li>
                            <code>{">"}</code> Maximum: Compare target and
                            source scores, and set target score to whichever is
                            higher.
                        </li>
                        <li>
                            <code>{"<"}</code> Minimum: Compare target and
                            source scores, and set target score to whichever is
                            lower.
                        </li>
                        <li>
                            <code>{"><"}</code> Swap: Swap target's score with
                            source's score.
                        </li>
                        <li>
                            <code>??=</code> Null Coalescing: If source's score
                            is null/undefined, set source's score to target's
                            score. (This operator works with NBT syntax)
                        </li>
                    </ul>
                    <CodeBlock>
                        <CodeText type="variable">{"$<variable>"}</CodeText>{" "}
                        <CodeText type="operator">{"<operator>"}</CodeText>{" "}
                        <CodeText type="number">{"<integer>"}</CodeText>; <br />
                        <CodeText type="variable">
                            {"$<variable>"}
                        </CodeText>{" "}
                        <CodeText type="operator">{"<operator>"}</CodeText>{" "}
                        <CodeText type="variable">{"$<variable>"}</CodeText>;
                        <br />
                        <CodeText type="variable">
                            {"$<variable>"}
                        </CodeText>{" "}
                        <CodeText type="operator">{"<operator>"}</CodeText>{" "}
                        objective
                        <CodeText type="operator">:</CodeText>
                        selector;
                    </CodeBlock>
                    <CodeBlock>
                        <CodeText type="variable">$my_variable</CodeText>{" "}
                        <CodeText type="operator">+=</CodeText> my_objective
                        <CodeText type="operator">:</CodeText>
                        <CodeText type="param">@s</CodeText>;
                    </CodeBlock>
                </div>
                <section id="variable_incrementation" />
                <div className="text-secondary-contrast text-xl md:text-4xl mt-3 md:mt-4">
                    Incrementation
                    <SectionLinkCopy sectionId="variable_incrementation" />
                </div>
                <div className="text-white text-base md:text-2xl mt-4 max-w-full">
                    <ul className="ml-4 md:ml-6 list-disc list-inside">
                        <li>
                            <code>
                                <CodeText type="variable">
                                    {"$<variable>"}
                                </CodeText>
                                <CodeText type="operator">++</CodeText>
                            </code>{" "}
                            is equivalent to{" "}
                            <code>
                                <CodeText type="variable">
                                    {"$<variable>"}
                                </CodeText>{" "}
                                <CodeText type="operator">+=</CodeText>{" "}
                                <CodeText type="number">1</CodeText>;
                            </code>
                        </li>
                        <li>
                            <code>
                                <CodeText type="variable">
                                    {"$<variable>"}
                                </CodeText>
                                <CodeText type="operator">--</CodeText>
                            </code>{" "}
                            is equivalent to{" "}
                            <code>
                                <CodeText type="variable">
                                    {"$<variable>"}
                                </CodeText>{" "}
                                <CodeText type="operator">-=</CodeText>{" "}
                                <CodeText type="number">1</CodeText>;
                            </code>
                        </li>
                    </ul>
                </div>
                <section id="get_variable" />
                <div className="text-secondary-contrast text-xl md:text-4xl mt-3 md:mt-4">
                    Get variable
                    <SectionLinkCopy sectionId="get_variable" />
                </div>
                <div className="text-white text-base md:text-2xl mt-4 max-w-full">
                    <p>
                        <Tab />
                        To use <code>/scoreboard players get</code>, for{" "}
                        <code>/execute store</code>, use{" "}
                        <code>
                            <CodeText type="function">.get</CodeText>()
                        </code>{" "}
                        method. Or simply use the variable itself
                    </p>
                    <CodeBlock>
                        execute store result entity{" "}
                        <CodeText type="param">@s</CodeText> Motion[
                        <CodeText type="number">0</CodeText>] double{" "}
                        <CodeText type="number">0.01</CodeText> run{" "}
                        <CodeText type="variable">$var</CodeText>
                        <CodeText type="operator">.</CodeText>
                        <CodeText type="function">get</CodeText>();
                    </CodeBlock>
                    <CodeBlock>
                        execute store result entity{" "}
                        <CodeText type="param">@s</CodeText> Motion[
                        <CodeText type="number">0</CodeText>] double{" "}
                        <CodeText type="number">0.01</CodeText> run{" "}
                        <CodeText type="variable">$var</CodeText>;
                    </CodeBlock>
                </div>
                <section id="convert_to_string" />
                <div className="text-secondary-contrast text-xl md:text-4xl mt-3 md:mt-4">
                    Convert to string
                    <SectionLinkCopy sectionId="convert_to_string" />
                </div>
                <div className="text-white text-base md:text-2xl mt-4 max-w-full">
                    <p>
                        <Tab />
                        Use{" "}
                        <Link to="/documentation/built-in-function#formatted_text">
                            FormattedText
                        </Link>{" "}
                        to convert a variable to string.
                    </p>
                </div>
                <section id="execute_store" />
                <div className="text-secondary-contrast text-xl md:text-4xl mt-3 md:mt-4">
                    Execute store
                    <SectionLinkCopy sectionId="execute_store" />
                </div>
                <div className="text-white text-base md:text-2xl mt-4 max-w-full">
                    <CodeBlock>
                        <CodeText type="variable">{"$<variable>"}</CodeText>{" "}
                        <CodeText type="operator">=</CodeText> {"<command>"}{" "}
                        <CodeText type="comment">
                            {"//"} execute store result
                        </CodeText>
                        <br />
                        <CodeText type="variable">
                            {"$<variable>"}
                        </CodeText>{" "}
                        <CodeText type="operator">?=</CodeText> {"<command>"}{" "}
                        <CodeText type="comment">
                            {"//"} execute store success
                        </CodeText>
                    </CodeBlock>
                    <p>
                        <Tab />
                        Example
                    </p>
                    <CodeBlock>
                        <CodeText type="variable">$my_var</CodeText>{" "}
                        <CodeText type="operator">=</CodeText> data get entity
                        @s SelectedItem.tag.my_var;
                    </CodeBlock>
                </div>
                <section id="expression_evaluation" />
                <div className="text-secondary-contrast text-xl md:text-4xl mt-3 md:mt-4">
                    Expression Evaluation
                    <SectionLinkCopy sectionId="expression_evaluation" />
                </div>
                <div className="text-white text-base md:text-2xl mt-4 max-w-full">
                    <p>
                        <Tab />
                        Evaluate an expression and store the result in a
                        variable. An expression may contain a command wrapped in
                        curly parenthesis. (Reminder: NBT syntax is also a
                        command)
                    </p>
                    <CodeBlock>
                        <CodeText type="variable">{"$<variable>"}</CodeText>{" "}
                        <CodeText type="operator">:{"<operator>"}</CodeText>{" "}
                        <CodeText type="variable">{"$<expression>"}</CodeText>{" "}
                    </CodeBlock>
                    <p>
                        <Tab />
                        Example
                    </p>
                    <CodeBlock>
                        <CodeText type="variable">$my_var</CodeText>{" "}
                        <CodeText type="operator">:=</CodeText> (
                        <CodeText type="number">5</CodeText>{" "}
                        <CodeText type="operator">+</CodeText>{" "}
                        <CodeText type="variable">$var2</CodeText>)
                        <CodeText type="operator">**</CodeText>
                        <CodeText type="number">2</CodeText>{" "}
                        <CodeText type="operator">/</CodeText> {"{"}
                        <CodeText type="class">my_storage</CodeText>
                        <CodeText type="operator">::</CodeText>
                        <CodeText type="param">path</CodeText>
                        <CodeText type="operator">.</CodeText>
                        <CodeText type="param">to</CodeText>
                        <CodeText type="operator">.</CodeText>
                        <CodeText type="param">element</CodeText>
                        {"}"} <CodeText type="operator">-</CodeText> {"{"}
                        say{" "}
                        <CodeText type="string">
                            "This command returns 1"
                        </CodeText>
                        {"}"};
                    </CodeBlock>
                </div>
            </section>
        </>
    );
};

export default Variable;
